import React, { useEffect, useState } from "react";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    Label,
    FormGroup,
    Form,
    Row,
    Col,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
    addOnboardingDocument,
    getOnboardingDocumentById,
    updateOnboardingDocument,
} from "model/OnboardingDocumentModel";

export const INITIAL_ONBOARDING_DOCUMENT_FORM = {
    documentName: "",
    status: "active",
    isForUSCitizens: false,
    isForNonUSCitizens: false,
    isDefault: false,
    tags: "",
};

const onboardingDocumentSchema = Yup.object().shape({
    documentName: Yup.string().trim().required("Document name is required"),
    status: Yup.string()
        .trim()
        .oneOf(["active", "deactive"], "Status is required")
        .required("Status is required"),
    isForUSCitizens: Yup.boolean(),
    isForNonUSCitizens: Yup.boolean(),
    isDefault: Yup.boolean(),
    tags: Yup.string().trim(),
});

const OnboardingDocumentAddEditModal = ({
    setOnboardingDocumentAddEditModalOpen,
    onboardingDocumentAddEditModalOpen,
    getOnboardingDocumentList,
    itemId,
    setFormData,
    formData,
}) => {
    const [formKey, setFormKey] = useState(0);
    const [errors, setErrors] = useState({});

    const closeModal = () => {
        setOnboardingDocumentAddEditModalOpen(false);
        setErrors({});
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        const fieldValue = type === "checkbox" ? checked : value;

        setFormData((previousForm) => ({
            ...previousForm,
            [name]: fieldValue,
        }));

        if (onboardingDocumentSchema.fields[name]) {
            setErrors((previousErrors) => ({ ...previousErrors, [name]: "" }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            await onboardingDocumentSchema.validate(formData, { abortEarly: false });
            setErrors({});
        } catch (validationError) {
            const validationErrors = {};
            validationError?.inner?.forEach((errorItem) => {
                if (errorItem?.path && !validationErrors[errorItem.path]) {
                    validationErrors[errorItem.path] = errorItem.message;
                }
            });
            setErrors(validationErrors);
            return;
        }

        const params = {
            documentName: String(formData?.documentName || "").trim(),
            status: formData?.status === "deactive" ? "deactive" : "active",
            isForUSCitizens: Boolean(formData?.isForUSCitizens),
            isForNonUSCitizens: Boolean(formData?.isForNonUSCitizens),
            isDefault: Boolean(formData?.isDefault),
            tags: String(formData?.tags || "").trim(),
        };

        const response = itemId
            ? await updateOnboardingDocument(itemId, params)
            : await addOnboardingDocument(params);

        if (response?.success) {
            toast.success(response?.message);
            closeModal();
            getOnboardingDocumentList?.();
        } else {
            toast.error(response?.message);
        }
    };

    const getFetchOnboardingDocumentById = async (id) => {
        const response = await getOnboardingDocumentById({ Id: id });
        if (response?.data) {
            const detail = response.data;
            setFormData({
                documentName: detail?.documentName || detail?.name || "",
                status:
                    String(detail?.status || "active").toLowerCase() === "deactive"
                        ? "deactive"
                        : "active",
                isForUSCitizens: Boolean(detail?.isForUSCitizens),
                isForNonUSCitizens: Boolean(detail?.isForNonUSCitizens),
                isDefault: Boolean(detail?.isDefault),
                tags: detail?.tags || "",
            });
            setFormKey((previousKey) => previousKey + 1);
        }
    };

    useEffect(() => {
        if (itemId && onboardingDocumentAddEditModalOpen) {
            getFetchOnboardingDocumentById(itemId);
        }
    }, [itemId, onboardingDocumentAddEditModalOpen]);

    return (
        <Modal
            isOpen={onboardingDocumentAddEditModalOpen}
            toggle={closeModal}
            backdrop="static"
            className="modal-full-height"
            size="md"
            centered
        >
            <ModalBody
                key={formKey}
                className="p-4"
                style={{ maxHeight: "95vh", overflowY: "auto" }}
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0 text-uppercase">
                        {itemId ? "Update Onboarding Document" : "Create Onboarding Document"}
                    </h5>
                    <Button close onClick={closeModal} />
                </div>
                <hr />

                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label>
                            Document Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            name="documentName"
                            defaultValue={formData?.documentName || ""}
                            onChange={handleChange}
                        />
                        {errors.documentName ? (
                            <small className="text-danger">{errors.documentName}</small>
                        ) : null}
                    </FormGroup>

                    <Row className="g-3 mb-3">
                        <Col md={6}>
                            <FormGroup check className="mb-0">
                                <Input
                                    type="checkbox"
                                    id="isForUSCitizens"
                                    name="isForUSCitizens"
                                    defaultChecked={Boolean(formData?.isForUSCitizens)}
                                    onChange={handleChange}
                                />
                                <Label check for="isForUSCitizens" className="ms-1">
                                    Is for US Citizens
                                </Label>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup check className="mb-0">
                                <Input
                                    type="checkbox"
                                    id="isForNonUSCitizens"
                                    name="isForNonUSCitizens"
                                    defaultChecked={Boolean(formData?.isForNonUSCitizens)}
                                    onChange={handleChange}
                                />
                                <Label check for="isForNonUSCitizens" className="ms-1">
                                    Is for Non-US Citizens
                                </Label>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="g-3 mb-3">
                    <Col md={6}>
                    <FormGroup className="mb-0">
                    <Input
                            type="checkbox"
                            id="isDefault"
                            name="isDefault"
                            defaultChecked={Boolean(formData?.isDefault)}
                            onChange={handleChange}
                        />
                        <Label className="ms-2">Is Default</Label>
                        </FormGroup>
                        </Col>
                        </Row>
                       
                    {/* </FormGroup> */}

                    <FormGroup>
                        <Label>
                            Status <span className="text-danger">*</span>
                        </Label>
                        <div className="input-box d-flex align-items-center justify-content-between">
                            <span className="text-muted">Status</span>
                            <div>
                                <Input
                                    type="radio"
                                    name="status"
                                    value="active"
                                    defaultChecked={formData?.status === "active"}
                                    onChange={handleChange}
                                />{" "}
                                Active
                                <Input
                                    type="radio"
                                    name="status"
                                    value="deactive"
                                    defaultChecked={formData?.status === "deactive"}
                                    onChange={handleChange}
                                    className="ms-3"
                                />{" "}
                                Deactive
                            </div>
                        </div>
                        {errors.status ? (
                            <small className="text-danger">{errors.status}</small>
                        ) : null}
                    </FormGroup>

                    <FormGroup>
                        <Label>Tags</Label>
                        <Input
                            type="text"
                            name="tags"
                            defaultValue={formData?.tags || ""}
                            onChange={handleChange}
                        />
                    </FormGroup>

                    <div className="text-end mt-3">
                        <Button color="primary" type="submit">
                            {/* <i className="mdi mdi-content-save me-1" aria-hidden /> */}
                            {itemId? 'Update' : 'Save'}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default OnboardingDocumentAddEditModal;
